import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GCBadgeSVG, SCBadgeSVG } from "./CurrencyBadge";
import { cn } from "@/lib/utils";
import { Clock, Sparkles } from 'lucide-react';

// Helper to format time remaining
const formatTimeRemaining = (targetDate: Date): { text: string; isUrgent: boolean } | null => {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  
  if (diff <= 0) return null;
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  if (days > 0) {
    return { text: `${days}d ${hours}h`, isUrgent: false };
  }
  
  const pad = (n: number) => n.toString().padStart(2, '0');
  return { 
    text: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
    isUrgent: hours < 1
  };
};


interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

/** Unified purchase item type */
interface PurchaseItem {
  id: string | number;
  title: string;
  price: number;
  original_price?: number | null;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  type: 'coin_package' | 'scheduled_offer' | 'promo_offer' | 'special_offer';
}

interface CompactGroupOfferCardProps {
  group: ScheduledOfferGroup;
  onPurchase: (item: PurchaseItem) => void;
  formatCoins: (amount: number | string | null | undefined) => string;
  formatPrice: (price: number | string | null | undefined) => string;
}

export const CompactGroupOfferCard = ({
  group,
  onPurchase,
  formatCoins,
  formatPrice,
}: CompactGroupOfferCardProps) => {
  // Sort offers by price (cheapest first) and limit to max 3
  const sortedOffers = useMemo(() => {
    return [...group.offers]
      .sort((a, b) => a.price - b.price)
      .slice(0, 3); // Max 3 offers
  }, [group.offers]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedOffer = sortedOffers[selectedIndex];

  if (!selectedOffer) return null;

  const totalGC = selectedOffer.gc_amount + selectedOffer.gc_bonus_amount;
  const totalSC = selectedOffer.sc_amount + selectedOffer.sc_bonus_amount;

  const isWelcomeGroup = group.is_welcome_group || sortedOffers.some(offer => offer.is_welcome_offer);
  const resetsAt = sortedOffers[0]?.resets_at;

  // Timer state for countdown
  const [timerData, setTimerData] = useState<{ text: string; isUrgent: boolean } | null>(null);
  
  useEffect(() => {
    if (!resetsAt) {
      setTimerData(null);
      return;
    }
    
    const targetDate = new Date(resetsAt);
    setTimerData(formatTimeRemaining(targetDate));
    
    const interval = setInterval(() => {
      const remaining = formatTimeRemaining(targetDate);
      setTimerData(remaining);
      if (!remaining) clearInterval(interval);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [resetsAt]);

  const handlePurchase = () => {
    onPurchase({
      id: selectedOffer.id,
      title: selectedOffer.title,
      price: selectedOffer.price,
      original_price: selectedOffer.original_price,
      gc_amount: selectedOffer.gc_amount,
      sc_amount: selectedOffer.sc_amount,
      gc_bonus_amount: selectedOffer.gc_bonus_amount,
      sc_bonus_amount: selectedOffer.sc_bonus_amount,
      type: 'scheduled_offer',
    });
  };

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all duration-300 h-[250px] group",
      isWelcomeGroup 
        ? "bg-gradient-to-br from-amber-900/90 via-yellow-800/80 to-amber-900/90"
        : "bg-gradient-to-br from-purple-950/80 via-purple-900/60 to-purple-800/50",
      isWelcomeGroup
        ? "border-2 border-yellow-400/70 hover:border-yellow-300"
        : "border-2 border-purple-500/50 hover:border-purple-400/80",
      isWelcomeGroup
        ? "shadow-lg shadow-yellow-500/40 hover:shadow-yellow-400/60"
        : "shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40",
      "hover-card-lift",
      // Extra styling for welcome groups
      isWelcomeGroup && "ring-2 ring-yellow-400/50 ring-offset-2 ring-offset-transparent"
    )}>
      {/* Welcome group animated border glow */}
      {isWelcomeGroup && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-400/20 via-amber-300/30 to-yellow-400/20 animate-pulse pointer-events-none" />
      )}
      
      {/* Top accent line with glow */}
      <div className={cn(
        "absolute top-0 left-0 right-0 opacity-80 group-hover:opacity-100 transition-opacity",
        isWelcomeGroup 
          ? "h-2 bg-gradient-to-r from-yellow-300 via-amber-200 to-yellow-300"
          : "h-1.5 bg-gradient-to-r from-purple-400 via-pink-300 to-purple-400"
      )} />
      
      {/* Welcome group sparkle decorations */}
      {isWelcomeGroup && (
        <>
          <Sparkles className="absolute top-3 right-3 h-5 w-5 text-yellow-300 animate-pulse z-20" />
          <Sparkles className="absolute bottom-16 left-3 h-4 w-4 text-amber-300 animate-pulse z-20" style={{ animationDelay: '0.5s' }} />
        </>
      )}
      
      {/* Shimmer effect - enhanced for welcome */}
      <div className={cn(
        "absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform pointer-events-none",
        isWelcomeGroup 
          ? "bg-gradient-to-r from-transparent via-yellow-200/20 to-transparent duration-700" 
          : "bg-gradient-to-r from-transparent via-white/5 to-transparent duration-1000"
      )} />

      <CardContent className="p-3 lg:p-4 h-full flex flex-col relative z-10">
        {/* Header row with badges - matching scheduled offer style */}
        <div className="flex items-start justify-between gap-1.5 mb-2">
          {isWelcomeGroup ? (
            <Badge className="font-black text-[9px] lg:text-xs px-2 lg:px-3 py-1 lg:py-1.5 shadow-md whitespace-nowrap bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-400 text-amber-900">
              🎁 Welcome • Pick 1
            </Badge>
          ) : (
            <div />
          )}
        </div>

        {/* Option selector - shows GC, SC, and price for each */}
        <div className="flex flex-col gap-1.5 flex-1">
          {sortedOffers.map((offer, index) => {
            const offerGC = offer.gc_amount + offer.gc_bonus_amount;
            const offerSC = offer.sc_amount + offer.sc_bonus_amount;
            const isSelected = index === selectedIndex;
            const canBuy = offer.can_purchase !== false && (offer.purchases_remaining ?? 1) > 0;

            return (
              <button
                key={offer.id}
                onClick={() => setSelectedIndex(index)}
                disabled={!canBuy}
                className={cn(
                  "w-full px-3 rounded-lg transition-all flex items-center justify-between flex-1",
                  isSelected
                    ? "bg-gradient-to-r from-amber-500/90 to-yellow-400/90 text-amber-900 ring-2 ring-amber-300 shadow-lg shadow-amber-500/30"
                    : canBuy
                      ? isWelcomeGroup
                        ? "bg-amber-800/50 text-amber-100 hover:bg-amber-700/60 border border-amber-500/30"
                        : "bg-purple-800/50 text-purple-100 hover:bg-purple-700/60 border border-purple-500/30"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              >
                {/* Left side: GC + SC */}
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-1">
                    <GCBadgeSVG size={20} />
                    <span className={cn("font-black text-sm", isSelected ? "text-amber-900" : "text-amber-300")}>
                      {formatCoins(offerGC)}
                    </span>
                  </div>
                  {offerSC > 0 && (
                    <>
                      <span className={cn("text-xs font-medium", isSelected ? "text-amber-800/70" : isWelcomeGroup ? "text-amber-400" : "text-purple-400")}>+</span>
                      <div className="flex items-center gap-1">
                        <SCBadgeSVG size={20} />
                        <span className={cn("font-bold text-xs", isSelected ? "text-green-700" : "text-green-400")}>
                          {formatCoins(offerSC)}
                        </span>
                        <span className={cn("font-semibold text-[10px]", isSelected ? "text-green-700" : "text-green-400")}>
                          FREE
                        </span>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Right side: Price */}
                <div className={cn(
                  "font-black text-sm px-2 py-0.5 rounded-md",
                  isSelected 
                    ? "bg-amber-900/20 text-amber-900" 
                    : isWelcomeGroup
                      ? "bg-amber-900/50 text-white"
                      : "bg-purple-900/50 text-white"
                )}>
                  {formatPrice(offer.price)}
                </div>
              </button>
            );
          })}
        </div>

        {/* Timer - matching scheduled offer style */}
        {timerData && (
          <div className={cn(
            "flex items-center justify-center gap-1.5 mt-2 mb-2 py-1 px-2 rounded-lg",
            timerData.isUrgent 
              ? "bg-red-500/20 animate-pulse" 
              : isWelcomeGroup ? "bg-amber-500/10" : "bg-purple-500/10"
          )}>
            <Clock className={cn(
              "h-3.5 w-3.5",
              timerData.isUrgent ? "text-red-400" : isWelcomeGroup ? "text-amber-400" : "text-purple-400"
            )} />
            <span className={cn(
              "text-xs font-semibold",
              timerData.isUrgent ? "text-red-400" : isWelcomeGroup ? "text-amber-400" : "text-purple-400"
            )}>
              Resets <span className="font-mono font-bold">{timerData.text}</span>
            </span>
          </div>
        )}

        {/* Purchase button - same size as scheduled offer */}
        <Button
          onClick={handlePurchase}
          disabled={!selectedOffer.can_purchase && selectedOffer.can_purchase !== undefined}
          className={cn(
            "w-full font-black py-2 rounded-xl text-sm h-11",
            !timerData && "mt-2",
            "transition-all duration-200 relative overflow-hidden group/btn",
            isWelcomeGroup 
              ? "bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 hover:from-yellow-400 hover:via-amber-300 hover:to-yellow-400 text-amber-900"
              : "bg-gradient-to-r from-purple-500 via-pink-400 to-purple-500 hover:from-purple-400 hover:via-pink-300 hover:to-purple-400 text-white",
            "shadow-lg hover:shadow-xl border-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            <span className="text-base">{formatPrice(selectedOffer.price)}</span>
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-500" />
        </Button>
      </CardContent>
    </Card>
  );
};
